import axios from "axios";

export interface User {
    id: string;
    name: string;
    email: string | null;
    role: string | null;
    isSystemAdministrator: boolean;
    // manuals role: "admin" | "editor" | "readonly" (from /api/manuals/users)
    manualsRole?: string;
}

export function displayName(u: User): string {
    const name = (u.name || "").trim();
    if (!name) return u.email || u.id.slice(0, 8);
    const parts = name.split(/\s+/);
    let out = parts.length >= 2 && parts[0].toLowerCase() === parts[1].toLowerCase()
        ? parts[0]
        : name;
    if (/^nhc/i.test(out)) {
        const rest = out.slice(3);
        out = rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : out;
    }
    return out;
}

export function initials(display: string): string {
    const parts = display.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return display.slice(0, 2).toUpperCase();
}

// email domains considered part of the organization — users with other domains
// get flagged when invited to restricted categories
export const ORG_EMAIL_DOMAINS = ["nationalhealthclaim.com"];

export function isExternalUser(u: User | undefined): boolean {
    const email = u?.email?.trim().toLowerCase();
    if (!email || !email.includes("@")) return false; // can't tell — don't flag
    const domain = email.split("@")[1];
    return !ORG_EMAIL_DOMAINS.includes(domain);
}

const AVATAR_COLORS = ["#3B82F6", "#EF4444", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899", "#06B6D4", "#F97316", "#14B8A6", "#A855F7"];

export function avatarColor(seed: string): string {
    let h = 0;
    for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
    return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

let usersCache: User[] | null = null;
let usersPromise: Promise<User[]> | null = null;

export function loadUsers(): Promise<User[]> {
    if (usersCache) return Promise.resolve(usersCache);
    if (usersPromise) return usersPromise;
    usersPromise = axios.get("/api/manuals/users").then(res => {
        if (res.data.success) {
            usersCache = res.data.items as User[];
            return usersCache;
        }
        return [];
    }).finally(() => { usersPromise = null; });
    return usersPromise;
}

export function labelForUserId(users: Map<string, User>, id: string | null | undefined): string {
    if (!id) return "Unknown";
    const u = users.get(id);
    return u ? displayName(u) : id.slice(0, 8);
}
